import { Link } from "react-router-dom";
import type { FeatureCard as FeatureCardModel } from "@/types/site";

interface FeaturePanelProps {
  card: FeatureCardModel;
}

const accentMap = {
  primary: "bg-primary/15 text-primary",
  secondary: "bg-secondary/15 text-secondary",
  tertiary: "bg-tertiary/15 text-tertiary",
};

export function FeaturePanel({ card }: FeaturePanelProps) {
  const accent = accentMap[card.accent ?? "primary"];
  const content = (
    <>
      {card.image ? (
        <div className="relative h-72 overflow-hidden">
          <img
            src={card.image}
            alt=""
            className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/35 to-transparent" />
        </div>
      ) : null}
      <div className="space-y-4 p-8">
        <span className={`inline-flex rounded-pill px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.26em] ${accent}`}>
          {card.eyebrow}
        </span>
        <div className="space-y-3">
          <h3 className="font-display text-2xl text-text">{card.title}</h3>
          <p className="leading-7 text-muted">{card.description}</p>
        </div>
        {card.bullets ? (
          <ul className="space-y-2 pt-1 text-sm uppercase tracking-[0.16em] text-muted">
            {card.bullets.map((bullet) => (
              <li key={bullet} className="flex items-center gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-tertiary" />
                {bullet}
              </li>
            ))}
          </ul>
        ) : null}
        {card.linkLabel ? (
          <div className="pt-3 font-label text-xs uppercase tracking-[0.24em] text-primary">
            {card.linkLabel} -&gt;
          </div>
        ) : null}
      </div>
    </>
  );

  if (card.href) {
    return (
      <Link
        to={card.href}
        className="group overflow-hidden rounded-[1.75rem] border border-outline/10 bg-surface-high shadow-ambient transition hover:-translate-y-1 hover:border-primary/20"
      >
        {content}
      </Link>
    );
  }

  return (
    <div className="group overflow-hidden rounded-[1.75rem] border border-outline/10 bg-surface-high shadow-ambient transition hover:-translate-y-1 hover:border-primary/20">
      {content}
    </div>
  );
}
